import React from "react";
import styled from "styled-components/macro";
import { Dropdown as RBDropdown } from "react-bootstrap";
import { EventName } from "utils/analytics/analyticsConstants";
import Dropdown from "ui/Dropdown";
import logout from "utils/logout";
import switchAccount from "utils/switchAccount";
import { useNavigate } from "react-router-dom";
import { theme } from "ui/theme";
import Avatar from "ui/eds/Avatar";

export interface NavigationDropdownProps {
  isOpen?: boolean;
  student: {
    id: string;
    name: string;
    isDemo: boolean;
    profileImage?: { id: string; url: string } | null;
  };
}

interface DropdownMenuProps {
  $avatarSize: "md" | "lg";
  $isOpen: boolean;
}

const $DropdownToggle = styled(RBDropdown.Toggle)<DropdownMenuProps>`
  &::after {
    margin-left: 0;
    color: ${theme.ColorSpace500};
  }

  &:active,
  &:hover,
  &:focus {
    & div::after {
      content: "";
      width: calc(
        ${({ $avatarSize }) =>
            $avatarSize === "md" ? "var(--eds-size-5)" : "var(--eds-size-6)"} +
          2px
      );
      height: calc(
        ${({ $avatarSize }) =>
            $avatarSize === "md" ? "var(--eds-size-5)" : "var(--eds-size-6)"} +
          2px
      );
      background: transparent;
      border-radius: 50%;
      position: absolute;
      border: 2px solid ${theme.ColorBlurple500};
    }
  }
`;

const $DropdownMenu = styled(RBDropdown.Menu)<DropdownMenuProps>`
  min-width: 0px !important;
  right: auto !important;
  left: 50% !important;
  transform: translate(
    ${(props) => (props.$isOpen ? "-50%" : "-20%")},
    ${(props) => (props.$avatarSize === "lg" ? "125%" : "100%")}
  ) !important;
`;

/**
 *  NavigationDropdown displays the user avatar and a dropdown when clicked
 */
export default function NavigationDropdown({
  student,
  isOpen,
}: NavigationDropdownProps) {
  const navigate = useNavigate();
  const avatarSize = isOpen ? "lg" : "md";
  return (
    <RBDropdown align="start">
      <$DropdownToggle
        aria-label="NavigationDropdown"
        id="navigation-dropdown"
        data-testid="navigation-dropdown-button"
        variant="icon"
        $avatarSize={avatarSize}
      >
        <Avatar
          size={avatarSize}
          userId={student.id}
          name={student.name}
          profileImage={student.profileImage?.url}
        />
      </$DropdownToggle>
      <$DropdownMenu $isOpen={isOpen} $avatarSize={avatarSize}>
        <Dropdown.Item
          href="/profile"
          onClick={(e) => {
            e.preventDefault();
            navigate("/profile");
          }}
          analyticsName="clicked_profile"
        >
          View profile
        </Dropdown.Item>
        {student.isDemo && (
          <Dropdown.Item
            onClick={() => switchAccount()}
            analyticsName={EventName.Navigation.CLICKED_EXIT_STUDENT_VIEW}
          >
            Exit student view
          </Dropdown.Item>
        )}
        {!student.isDemo && (
          // demo students will exit the student view first to sign out
          <Dropdown.Item onClick={() => logout()} analyticsName="logged_out">
            Sign out
          </Dropdown.Item>
        )}
      </$DropdownMenu>
    </RBDropdown>
  );
}
